import { AxiosError } from 'axios';
import { toaster } from '@optiaxiom/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import {
  approveSherlockExecution,
  clearSherlockRebase,
  enableSherlock,
  getSherlockAgentStatus,
  rebaseSherlockModel,
  rejectSherlockExecution,
  SherlockEnableRequest,
  SherlockRebaseModelRequest,
  updateSherlockAutoTerminate,
  updateSherlockSensitivity,
} from '../services/sherlock';
import { getSpecializedAgentKey, updateAgentBaselineScore } from '../services/specialized-agent';

export function useSherlockStatus({
  agentId,
  internalVersion,
}: {
  agentId: string;
  internalVersion: number;
}) {
  const { data, isPending, error } = useQuery({
    queryKey: getSherlockAgentStatus.key({ agentId, internalVersion }),
    queryFn: getSherlockAgentStatus({ agentId, internalVersion }),
    enabled: !!agentId && !!internalVersion,
    retry: false, // Don't retry on failure, we handle 404 separately
  });

  return {
    status: data || {
      agent_id: agentId,
      internal_version: internalVersion,
      sherlock_enabled: false,
      sensitivity_multiplier: 1,
      auto_reject_enabled: true,
      needs_retrain: false,
      message: '',
      approved_executions: 0,
      total_executions: 0,
      anomalies_detected: 0,
      executions_stopped: 0,
      avg_quality_score: null,
      execution_status_breakdown: {
        rescued: 0,
        passed: 0,
        warning: 0,
        failed: 0,
        terminated: 0,
        learning: 0,
        unapproved: 0,
      },
      current_mode: 'learn',
      rebased_from_version: null,
      learn_mode_threshold: 20,
      observer_mode_threshold: 50,
      learn_mode_remaining: 20,
      observer_mode_remaining: 50,
    },
    isPending,
    error,
    isNotConfigured: error instanceof AxiosError && error?.response?.status === 404,
  };
}

export function useEnableSherlock(
  agentId: string,
  internalVersion: number,
  onSuccess?: () => void
) {
  const queryClient = useQueryClient();

  const { mutate, isPending, error, data } = useMutation({
    mutationFn: async (request: SherlockEnableRequest = {}) => {
      return enableSherlock({ agentId, internalVersion, request })();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getSherlockAgentStatus.key({ agentId, internalVersion }),
      });
      toaster.create('Execution guardrails enabled successfully', { type: 'success' });
      onSuccess?.();
    },
    onError: () => {
      toaster.create('Failed to enable execution guardrails', { type: 'danger' });
    },
  });

  return { mutate, isPending, error, data };
}

export function useUpdateSherlockSensitivity({
  agentId,
  internalVersion,
  onSuccess,
}: {
  agentId: string;
  internalVersion: number;
  onSuccess?: () => void;
}) {
  const queryClient = useQueryClient();

  const { mutate, isPending, error, data } = useMutation({
    mutationFn: async (multiplier: number) => {
      return updateSherlockSensitivity({ agentId, internalVersion, request: { multiplier } })();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getSherlockAgentStatus.key({ agentId, internalVersion }),
      });
      toaster.create('Sensitivity saved. Changes take effect immediately.', {
        type: 'success',
      });
      onSuccess?.();
    },
    onError: () => {
      toaster.create('Failed to update sensitivity', { type: 'danger' });
    },
  });

  return { mutate, isPending, error, data };
}

export function useUpdateSherlockAutoTerminate({
  agentId,
  internalVersion,
  onSuccess,
}: {
  agentId: string;
  internalVersion: number;
  onSuccess?: () => void;
}) {
  const queryClient = useQueryClient();

  const { mutate, isPending, error, data } = useMutation({
    mutationFn: async (enabled: boolean) => {
      return updateSherlockAutoTerminate({ agentId, internalVersion, request: { enabled } })();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: getSherlockAgentStatus.key({ agentId, internalVersion }),
      });
      toaster.create('Auto-terminate setting updated successfully', { type: 'success' });
      onSuccess?.();
    },
    onError: () => {
      toaster.create('Failed to update auto-terminate setting', { type: 'danger' });
    },
  });

  return { mutate, isPending, error, data };
}

export function useRebaseSherlockModel({
  agentId,
  targetVersion,
}: {
  agentId: string;
  targetVersion: number;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (request: SherlockRebaseModelRequest) => {
      return rebaseSherlockModel({ agentId, targetVersion, request })();
    },
    onSuccess: data => {
      // Invalidate status to refresh UI
      queryClient.invalidateQueries({
        queryKey: getSherlockAgentStatus.key({
          agentId,
          internalVersion: targetVersion,
        }),
      });

      toaster.create(data.message, { type: 'success' });
    },
    onError: () => {
      toaster.create('Failed to rebase Sherlock model', { type: 'danger' });
    },
  });
}

export function useClearSherlockRebase({
  agentId,
  internalVersion,
}: {
  agentId: string;
  internalVersion: number;
}) {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async () => {
      return clearSherlockRebase({ agentId, internalVersion })();
    },
    onSuccess: data => {
      queryClient.invalidateQueries({
        queryKey: getSherlockAgentStatus.key({ agentId, internalVersion }),
      });
      toaster.create(data.message, { type: 'success' });
    },
    onError: () => {
      toaster.create('Failed to remove rebase link', { type: 'danger' });
    },
  });
}

export function useUpdateBaselineScore({
  instanceId,
  agentId,
  onSuccess,
}: {
  instanceId: string;
  agentId: string;
  onSuccess?: () => void;
}) {
  const queryClient = useQueryClient();

  const { mutate, isPending, error, data } = useMutation({
    mutationFn: async (baselineScore: number) => {
      return updateAgentBaselineScore(instanceId, agentId, baselineScore)();
    },
    onSuccess: () => {
      // Invalidate agent queries to refetch updated data
      queryClient.invalidateQueries({
        queryKey: getSpecializedAgentKey(agentId),
      });
      toaster.create('Baseline evaluation score updated successfully', { type: 'success' });
      onSuccess?.();
    },
    onError: () => {
      toaster.create('Failed to update baseline evaluation score', { type: 'danger' });
    },
  });

  return { mutate, isPending, error, data };
}

export function useRejectSherlockExecution({ onSuccess }: { onSuccess?: () => void } = {}) {
  const queryClient = useQueryClient();

  const { mutate, isPending, error } = useMutation({
    mutationFn: async ({ executionId, reason }: { executionId: string; reason?: string }) => {
      return rejectSherlockExecution({ executionId, request: { reason } })();
    },
    onSuccess: () => {
      // Invalidate execution queries to refetch updated data
      queryClient.invalidateQueries({ queryKey: ['opal:sherlock-agent-status'] });
      queryClient.invalidateQueries({ queryKey: ['opal:specialized-agent-execution'] });
      toaster.create('Execution rejected from guardrail training', { type: 'success' });
      onSuccess?.();
    },
    onError: () => {
      toaster.create('Failed to reject execution', { type: 'danger' });
    },
  });

  return { mutate, isPending, error };
}

export function useApproveSherlockExecution({ onSuccess }: { onSuccess?: () => void } = {}) {
  const queryClient = useQueryClient();

  const { mutate, isPending, error } = useMutation({
    mutationFn: async ({ executionId }: { executionId: string }) => {
      return approveSherlockExecution({ executionId })();
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['opal:sherlock-agent-status'] });
      queryClient.invalidateQueries({ queryKey: ['opal:specialized-agent-execution'] });
      toaster.create('Execution approved for guardrail training', { type: 'success' });
      onSuccess?.();
    },
    onError: () => {
      toaster.create('Failed to approve execution', { type: 'danger' });
    },
  });

  return { mutate, isPending, error };
}
